/**
 * Section: Tw step cards
 * -----------------------------------------------------------------------------
 * Keeps the mobile carousel dots in sync with the scroll-snap list and
 * scrolls to a card when its dot is clicked. Scrolling itself is CSS only.
 * Wrapped in a guard so a second copy of the section doesn't redefine it.
 *
 */

if (!customElements.get('tw-step-cards-slider')) {

  /**
   * Custom element wrapping the card list and its dots.
   */
  class TwStepCardsSlider extends HTMLElement {

    /**
     * Cache elements and bind listeners.
     */
    connectedCallback() {
      this.list = this.querySelector('[js-step-cards="list"]')
      this.cards = [...this.querySelectorAll('[js-step-cards="card"]')]
      this.dots = [...this.querySelectorAll('[js-step-cards="dot"]')]

      if (!this.list || !this.dots.length) {
        return
      }

      this.carouselQuery = window.matchMedia('(max-width: 680px)')
      this.handleClick = this.handleClick.bind(this)
      this.handleQueryChange = this.handleQueryChange.bind(this)
      this.update = this.update.bind(this)

      this.addEventListener('click', this.handleClick)
      this.list.addEventListener('scroll', this.update, { passive: true })
      this.list.addEventListener('scrollend', this.update, { passive: true })
      this.carouselQuery.addEventListener('change', this.handleQueryChange)
      this.update()
    }

    /**
     * Remove listeners when the section is removed in the theme editor.
     */
    disconnectedCallback() {
      if (!this.list || !this.dots.length) {
        return
      }

      this.removeEventListener('click', this.handleClick)
      this.list.removeEventListener('scroll', this.update)
      this.list.removeEventListener('scrollend', this.update)
      this.carouselQuery.removeEventListener('change', this.handleQueryChange)
    }

    /**
     * Scroll to the card that matches the clicked dot.
     * @param {MouseEvent} event - Click event from inside the element.
     */
    handleClick(event) {
      const dot = event.target.closest('[js-step-cards="dot"]')

      if (!dot || !this.carouselQuery.matches) {
        return
      }

      const card = this.cards[this.dots.indexOf(dot)]

      if (!card) {
        return
      }

      const offset = card.getBoundingClientRect().left -
        this.list.getBoundingClientRect().left -
        parseFloat(window.getComputedStyle(this.list).scrollPaddingInlineStart)

      this.list.scrollTo({
        behavior: 'instant',
        left: this.list.scrollLeft + offset,
      })

      this.update()
    }

    /**
     * Reset the scroll position when leaving the carousel layout.
     */
    handleQueryChange() {
      if (!this.carouselQuery.matches) {
        this.list.scrollLeft = 0
      }

      this.update()
    }

    /**
     * Mark the dot of the card nearest the start of the list as current.
     */
    update() {
      const start = this.list.getBoundingClientRect().left +
        parseFloat(window.getComputedStyle(this.list).paddingInlineStart)

      let nearestIndex = 0
      let nearestDistance = Infinity

      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - start)

        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestIndex = index
        }
      })

      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === nearestIndex))
      })
    }
  }

  customElements.define('tw-step-cards-slider', TwStepCardsSlider)
}
